import { useState, type FormEvent } from 'react';
import { toast } from 'sonner';
import { fieldErrorsOf } from '@/api/client';
import { useClientMutations } from '@/api/hooks';
import type { ClientOrg } from '@/api/schemasMembers';
import { FormField, fieldA11y } from '@/components/FormField';
import { Icon } from '@/components/Icon';
import { ProblemAlert } from '@/components/ProblemAlert';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { stagger } from '@/lib/motion';
import { detailsFieldOf, detailsPatch, validateDetails, type DetailsValues, type FieldErrors } from './clientLifecycleModel';

type Errors = FieldErrors<'name' | 'emailDomains'>;

function useDetailsForm(org: ClientOrg) {
  const { update } = useClientMutations(org.id);
  const [values, setValues] = useState<DetailsValues>({ name: org.name, domains: org.emailDomains.join(', ') });
  const [touched, setTouched] = useState(false);
  const [server, setServer] = useState<Errors>({});
  const local = validateDetails(values);
  const errors: Errors = touched ? { ...server, ...local } : server;
  const patch = detailsPatch(org, values);
  const set = (p: Partial<DetailsValues>) => { setServer({}); setValues((v) => ({ ...v, ...p })); };
  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setTouched(true);
    if (Object.keys(local).length || !patch) return;
    try {
      const saved = await update.mutateAsync(patch);
      setValues({ name: saved.name, domains: saved.emailDomains.join(', ') });
      setTouched(false);
      toast.success(`${saved.name} saved`, { description: patch.emailDomains ? 'Members keep their role: the domains decide how future invitations treat people.' : undefined });
    } catch (err) {
      const mapped: Errors = {};
      for (const [field, message] of Object.entries(fieldErrorsOf(err))) {
        const target = detailsFieldOf(field);
        if (target) mapped[target] = message;
      }
      setServer(mapped);
    }
  };
  return { values, errors, set, submit, dirty: Boolean(patch), pending: update.isPending, problem: Object.keys(server).length ? null : update.error };
}

/** Name and email domains of a client (`PATCH /platform/orgs/{orgId}`); the slug is shown fixed, with why. */
export function ClientDetailsCard({ org, id, index }: { org: ClientOrg; id: string; index: number }) {
  const f = useDetailsForm(org);
  return (
    <Card id={id} data-testid="client-details" className="enter scroll-mt-24" style={stagger(index)}>
      <form onSubmit={(e) => void f.submit(e)} noValidate aria-label="Client details" className="contents">
        <CardHeader>
          <CardTitle>Details</CardTitle>
          <CardDescription>How the client shows in Hangar, and which email domains are its own</CardDescription>
        </CardHeader>
        <CardContent className="flex flex-col gap-4">
          <div className="grid gap-4 sm:grid-cols-2">
            <FormField id="client-edit-name" label="Client name" required error={f.errors.name}>
              <Input data-testid="client-edit-name" value={f.values.name} maxLength={120} autoComplete="off" onChange={(e) => f.set({ name: e.target.value })} {...fieldA11y('client-edit-name', f.errors.name)} />
            </FormField>
            <SlugField slug={org.slug} />
          </div>
          <FormField id="client-edit-domains" label="Email domains" required error={f.errors.emailDomains} hint="Separate with commas. People at these domains are invited as members; everyone else only as external guests. Existing members keep their role.">
            <Input data-testid="client-edit-domains" value={f.values.domains} autoComplete="off" spellCheck={false} className="font-mono sm:text-[13px]" placeholder="acme.com, acme.io" onChange={(e) => f.set({ domains: e.target.value })} {...fieldA11y('client-edit-domains', f.errors.emailDomains, 'hint')} />
          </FormField>
          <ProblemAlert error={f.problem} testId="client-details-problem" />
        </CardContent>
        <CardFooter className="justify-end gap-3">
          {f.dirty ? <span className="text-xs text-subtle">Unsaved changes</span> : null}
          <Button type="submit" variant="outline" data-testid="client-details-save" disabled={!f.dirty || f.pending} className="max-sm:h-11">
            {f.pending ? 'Saving…' : 'Save'}
          </Button>
        </CardFooter>
      </form>
    </Card>
  );
}

function SlugField({ slug }: { slug: string }) {
  return (
    <FormField id="client-edit-slug" label="Slug" hint="Fixed: it names the client's Expo organization and prefixes its Bitrise apps" trailing={<Icon name="lock" size={14} label="Locked" className="text-subtle" />}>
      <div className="relative">
        <Input id="client-edit-slug" data-testid="client-edit-slug" value={slug} readOnly aria-readonly className="cursor-not-allowed bg-secondary pr-9 font-mono text-muted-foreground sm:text-[13px]" aria-describedby="client-edit-slug-hint" />
        <Icon name="lock" size={16} className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-subtle" />
      </div>
    </FormField>
  );
}
